import React, { useEffect, Suspense } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';

import { isBrowser } from 'react-device-detect';
import ReactGA from 'react-ga4';
import { ReactNotifications } from 'react-notifications-component';
import { createGlobalStyle } from 'styled-components';
import history from '../utils/HistoryService';

// Common components
import Login from '../components/common/login/Login';
import Logout from '../components/common/login/Logout';
import Unauthorized from '../components/common/errors/Unauthorized';
import LoadingIndicator from '../components/core/LoadingIndicator/LoadingIndicator';
import Callback from '../components/common/login/Callback';
import ErrorBoundary from '../components/common/errors/ErrorBoundary';
import PrivateRoute from './PrivateRoute';
import { useAuth0 } from '../providers/Auth0Provider';
import NotificationsService from '../utils/NotificationsService';

// desktop components
const DesktopDashboard = React.lazy(() =>
  import(
    /* webpackChunkName: "desktop" */ '../components/desktop/DesktopDashboard'
  )
);

// mobile components
const MobileDashboard = React.lazy(() =>
  import(
    /* webpackChunkName: "mobile" */ '../components/mobile/MobileDashboard'
  )
);

history.listen((location) => {
  // History.listen is for changes on the page, not for the initial page load.
  ReactGA.send({
    hitType: 'pageview',
    page: location.pathname,
    title: 'CoVadem Digital Platform',
  });
});

const AppRouter = () => {
  const { loading } = useAuth0();

  useEffect(() => {
    // This send is used for the initial page load, for changes on the page we use history.listen
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: 'CoVadem Digital Platform',
    });
    let activeOfflineNotificationId;

    const triggerOfflineNotification = () => {
      activeOfflineNotificationId =
        NotificationsService.triggerOfflineNotification();
    };

    const triggerRemoveOfflineNotification = () => {
      NotificationsService.removeNotification(activeOfflineNotificationId);
      activeOfflineNotificationId = null;
    };

    if (!window.navigator.onLine) {
      triggerOfflineNotification();
    }

    window.addEventListener('offline', triggerOfflineNotification);
    window.addEventListener('online', triggerRemoveOfflineNotification);

    return () => {
      window.removeEventListener('offline', triggerOfflineNotification);
      window.removeEventListener('online', triggerRemoveOfflineNotification);
    };
  }, []);

  const renderRoutes = () => {
    if (loading) {
      return <LoadingIndicator />;
    } else {
      return (
        <Switch>
          <Route exact path="/" render={(props) => <Login {...props} />} />
          <Route path="/logout" render={(props) => <Logout {...props} />} />
          <Route path="/callback" render={(props) => <Callback {...props} />} />
          <Route
            path="/unauthorized"
            render={(props) => <Unauthorized {...props} />}
          />
          {isBrowser ? (
            <PrivateRoute path="/desktop" component={DesktopDashboard} />
          ) : null}
          {!isBrowser ? (
            <PrivateRoute path="/mobile" component={MobileDashboard} />
          ) : null}
          <Redirect
            from={!isBrowser ? '/desktop' : '/mobile'}
            to={isBrowser ? '/desktop/trip' : '/mobile/trip'}
          />
          <Redirect from="*" to="/desktop/trip" />
        </Switch>
      );
    }
  };

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingIndicator />}>
        <Router history={history}>
          <GlobalStyle />
          <ReactNotifications />
          {renderRoutes()}
        </Router>
      </Suspense>
    </ErrorBoundary>
  );
};

// TODO: this is temporary, visibility of outlines should be updated
const GlobalStyle = createGlobalStyle`
  body button:focus {
    outline: 0;
  }

  /* react-notifications related classes START */
  .notification-container-top-right {
    width: unset;
  }
  .fadeIn {
    animation: fadeIn;
    animation-duration: 1s;
  }
  .fadeOut {
    animation: fadeOut;
    animation-duration: 1s;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }

    to {
      opacity: 0;
    }
  }
  /* react-notifications related classes END */

  @media only screen and (min-width: 768px) {
    body ::-webkit-scrollbar {
      width: 11px;
    }
    body {
      scrollbar-width: thin;
      scrollbar-color: #14bad8 #2f3a43;
    }
    body ::-webkit-scrollbar-track {
      background: #2f3a43;
    }
    body ::-webkit-scrollbar-thumb {
      background-color: #14bad8;
      border-radius: 6px;
      border: 3px solid #2f3a43;
    }
  }
 `;

export default AppRouter;
